import type { LoggingFlowReviewItem } from "../lib/logging-flow";
import { isOffAbortError, offProductImageUrl } from "../lib/openfoodfacts-client";
import type { NewPlateItem, PlateItemSourceRef } from "../lib/plate";
import type {
  CustomFoodView,
  FavoriteView,
  FrequentFoodView,
  LibraryEntry,
  MemoryView,
  RecipeView,
} from "./library-utils";
import {
  customFoodDataSource,
  customFoodToReviewItem,
  entryToReviewItem,
  favoriteToReviewItem,
  filterRows,
  memoryToReviewItem,
  recipePerServing,
  reviewItemToPlateItem,
} from "./library-utils";

// Food Search pure logic (plan todo 17). Every interactive behavior of the
// search screen funnels through here so the repo's node-env test pattern
// (renderToStaticMarkup + pure functions, NO jsdom) can assert tab filtering,
// serving scaling, OFF submit gating, and review/plate mapping. Unknown
// nutrition stays undefined and is NEVER zero-filled. RELATIVE imports only
// (the "@/" alias breaks vitest).

export type SearchTabId =
  | "recent"
  | "frequent"
  | "memories"
  | "favourites"
  | "custom"
  | "recipes"
  | "verified";

export interface SearchTab {
  readonly id: SearchTabId;
  readonly label: string;
  readonly testId: string;
}

export const SEARCH_TABS: readonly SearchTab[] = [
  { id: "recent", label: "Recent", testId: "food-search-tab-recent" },
  { id: "frequent", label: "Frequent", testId: "food-search-tab-frequent" },
  { id: "memories", label: "Memories", testId: "food-search-tab-memories" },
  { id: "favourites", label: "Favourites", testId: "food-search-tab-favourites" },
  { id: "custom", label: "Custom foods", testId: "food-search-tab-custom" },
  { id: "recipes", label: "Recipes", testId: "food-search-tab-recipes" },
  { id: "verified", label: "Verified", testId: "food-search-tab-verified" },
];

// Structural mirror of the MappedOffProduct shape from the browser-direct
// client (src/lib/openfoodfacts-client.ts).
export interface OffNutritionView {
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
}

export interface OffProductView {
  readonly barcode?: string;
  readonly name?: string;
  readonly brands?: string;
  readonly servingSize?: string;
  readonly imageUrl?: string;
  readonly imageFrontUrl?: string;
  readonly per100g: OffNutritionView;
  readonly perServing: OffNutritionView;
  readonly incomplete: boolean;
  readonly noNutritionData: boolean;
  readonly hasBarcode: boolean;
}

// One normalized search result across every section. reviewItem carries the
// nutrition for ONE base serving (the servingLabel unit); the serving
// selector scales from there.
export interface SearchResultModel {
  readonly key: string;
  readonly name: string;
  readonly subtitle?: string;
  readonly dataSource?: string;
  // Base nutrition for one servingLabel unit (unknown fields undefined).
  readonly reviewItem: LoggingFlowReviewItem;
  readonly servingLabel: string;
  readonly defaultQuantity: number;
  // Verified-database results only: resolved OFF photo (front > main).
  readonly imageUrl?: string;
  // Custom foods: gram weight of one serving (enables grams mode).
  readonly servingWeightG?: number;
  // OFF per-100g base (enables grams mode against a 100 g reference).
  readonly gramsBaseG?: number;
  readonly sourceRef?: PlateItemSourceRef;
}

function spreadDefined<T extends Record<string, number | string | undefined>>(
  source: T,
): Partial<T> {
  const out: Partial<T> = {};
  for (const [key, value] of Object.entries(source)) {
    if (value !== undefined) {
      (out as Record<string, unknown>)[key] = value;
    }
  }
  return out;
}

// ── Per-kind result mappers ─────────────────────────────────────────────────

export function recentEntryToResult(entry: LibraryEntry): SearchResultModel {
  return {
    key: `recent-${entry._id}`,
    name: entry.name,
    ...spreadDefined({ dataSource: entry.dataSource }),
    reviewItem: entryToReviewItem(entry),
    servingLabel: entry.unit ?? "serving",
    defaultQuantity: entry.quantity ?? 1,
  };
}

export function frequentEntryToResult(entry: FrequentFoodView): SearchResultModel {
  const base = recentEntryToResult(entry);
  return {
    ...base,
    key: `frequent-${entry._id}`,
    ...(entry.count === undefined ? {} : { subtitle: `Logged ${entry.count} times recently` }),
  };
}

export function memoryToResult(memory: MemoryView): SearchResultModel {
  return {
    key: `memory-${memory._id}`,
    name: memory.foodName,
    dataSource: "memory",
    reviewItem: memoryToReviewItem(memory),
    servingLabel: memory.servingSize ?? "serving",
    defaultQuantity: 1,
    sourceRef: { kind: "memory", id: memory._id },
  };
}

const FAVORITE_SOURCE_REF_KINDS: Record<string, PlateItemSourceRef["kind"]> = {
  customFood: "customFood",
  recipe: "recipe",
  foodMemory: "memory",
};

export function favoriteToResult(favorite: FavoriteView): SearchResultModel {
  const refKind = FAVORITE_SOURCE_REF_KINDS[favorite.kind];
  return {
    key: `fav-${favorite.kind}-${favorite.refId}`,
    name: favorite.name,
    dataSource: favorite.kind === "foodMemory" ? "memory" : "user-entered",
    reviewItem: favoriteToReviewItem(favorite),
    servingLabel: favorite.servingLabel ?? "serving",
    defaultQuantity: 1,
    ...(refKind === undefined ? {} : { sourceRef: { kind: refKind, id: favorite.refId } }),
  };
}

export function customFoodToResult(food: CustomFoodView): SearchResultModel {
  return {
    key: `custom-${food._id}`,
    name: food.name,
    ...spreadDefined({
      subtitle: food.brand,
      dataSource: customFoodDataSource(food.source),
    }),
    reviewItem: customFoodToReviewItem(food),
    servingLabel: food.servingUnit,
    defaultQuantity: 1,
    ...(food.servingWeightG === undefined ? {} : { servingWeightG: food.servingWeightG }),
    sourceRef: { kind: "customFood", id: food._id },
  };
}

// Recipes chip "Saved recipe" only when per-serving nutrition is complete;
// incomplete recipes carry the "Incomplete nutrition" badge instead.
export function recipeToResult(recipe: RecipeView): SearchResultModel {
  const perServing = recipePerServing(recipe);
  const complete = recipe.totals?.complete === true;
  const dataSource = complete ? "saved-recipe" : "incomplete";
  return {
    key: `recipe-${recipe._id}`,
    name: recipe.name,
    ...(recipe.servings === undefined ? {} : { subtitle: `Makes ${recipe.servings} servings` }),
    dataSource,
    reviewItem: {
      name: recipe.name,
      quantity: 1,
      unit: "serving",
      ...spreadDefined({
        calories: perServing?.calories,
        proteinG: perServing?.proteinG,
        carbsG: perServing?.carbsG,
        fatG: perServing?.fatG,
        fiberG: perServing?.fiberG,
      }),
      dataSource,
      confidence: complete ? "high" : "low",
    },
    servingLabel: "serving",
    defaultQuantity: 1,
    sourceRef: { kind: "recipe", id: recipe._id },
  };
}

// "Verified database" = products with complete nutrition; incomplete results
// are badged "Incomplete nutrition". Base serving is perServing when it has
// calories, otherwise the per-100g reference (which then enables grams mode).
export function offProductToResult(product: OffProductView, index: number): SearchResultModel {
  const name = product.name ?? "Unknown product";
  const hasServing = product.perServing.calories !== undefined;
  const base = hasServing ? product.perServing : product.per100g;
  const servingLabel = hasServing ? (product.servingSize ?? "serving") : "100 g";
  const dataSource = product.incomplete ? "incomplete" : "verified-database";
  return {
    key: `off-${product.barcode ?? `idx-${index}`}`,
    name,
    ...spreadDefined({ subtitle: product.brands }),
    dataSource,
    reviewItem: {
      name,
      quantity: 1,
      unit: servingLabel,
      ...spreadDefined({
        calories: base.calories,
        proteinG: base.proteinG,
        carbsG: base.carbsG,
        fatG: base.fatG,
        fiberG: base.fiberG,
      }),
      dataSource,
      confidence: product.incomplete ? "low" : "high",
    },
    servingLabel,
    defaultQuantity: 1,
    ...spreadDefined({ imageUrl: offProductImageUrl(product) }),
    ...(hasServing ? {} : { gramsBaseG: 100 }),
    sourceRef: { kind: "off", id: product.barcode ?? name },
  };
}

// ── Local section filtering (instant; the 300ms debounce only re-arms the
//    server-backed memory/custom-food search queries in the wired screen) ────

export interface SearchDataSets {
  readonly recent?: readonly LibraryEntry[];
  readonly frequent?: readonly FrequentFoodView[];
  readonly memories?: readonly MemoryView[];
  readonly favourites?: readonly FavoriteView[];
  readonly custom?: readonly CustomFoodView[];
  readonly recipes?: readonly RecipeView[];
}

export type LocalSearchTabId = Exclude<SearchTabId, "verified">;

// Returns undefined while the backing query is still loading (skeleton
// state), otherwise the mapped rows filtered case-insensitively by name.
export function resultsForTab(
  tab: LocalSearchTabId,
  data: SearchDataSets,
  query: string,
): readonly SearchResultModel[] | undefined {
  const filter = (rows: readonly SearchResultModel[]) => filterRows(rows, query, (row) => row.name);
  switch (tab) {
    case "recent":
      return data.recent === undefined ? undefined : filter(data.recent.map(recentEntryToResult));
    case "frequent":
      return data.frequent === undefined ? undefined : filter(data.frequent.map(frequentEntryToResult));
    case "memories":
      return data.memories === undefined ? undefined : filter(data.memories.map(memoryToResult));
    case "favourites":
      return data.favourites === undefined ? undefined : filter(data.favourites.map(favoriteToResult));
    case "custom":
      return data.custom === undefined ? undefined : filter(data.custom.map(customFoodToResult));
    case "recipes":
      return data.recipes === undefined ? undefined : filter(data.recipes.map(recipeToResult));
  }
}

// ── Serving / quantity selection ────────────────────────────────────────────

export interface ServingSelection {
  readonly mode: "servings" | "grams";
  readonly quantity: number;
}

export function gramsAvailable(result: SearchResultModel): boolean {
  return result.servingWeightG !== undefined || result.gramsBaseG !== undefined;
}

export function gramsBase(result: SearchResultModel): number {
  return result.servingWeightG ?? result.gramsBaseG ?? 100;
}

export function defaultServingSelection(result: SearchResultModel): ServingSelection {
  return { mode: "servings", quantity: result.defaultQuantity };
}

// Switching modes resets the quantity to a sensible equivalent of one base
// serving so the displayed nutrition stays continuous.
export function selectionForMode(
  result: SearchResultModel,
  mode: ServingSelection["mode"],
): ServingSelection {
  return mode === "grams"
    ? { mode, quantity: gramsBase(result) }
    : { mode, quantity: result.defaultQuantity };
}

export function stepQuantity(
  selection: ServingSelection,
  direction: 1 | -1,
): ServingSelection {
  const step = selection.mode === "grams" ? 10 : 0.5;
  const next = Math.round((selection.quantity + direction * step) * 10) / 10;
  return { ...selection, quantity: Math.max(step, next) };
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

// Scales only the nutrition fields; quantity/unit/dataSource/confidence pass
// through. Undefined nutrients stay undefined at ANY factor.
export function scaleNutrition(
  item: LoggingFlowReviewItem,
  factor: number,
): LoggingFlowReviewItem {
  const scale = (value: number | undefined): number | undefined =>
    value === undefined ? undefined : round1(value * factor);
  return {
    ...item,
    ...spreadDefined({
      calories: scale(item.calories),
      proteinG: scale(item.proteinG),
      carbsG: scale(item.carbsG),
      fatG: scale(item.fatG),
      fiberG: scale(item.fiberG),
    }),
  };
}

export function resultToReviewItem(
  result: SearchResultModel,
  selection: ServingSelection,
): LoggingFlowReviewItem {
  const factor =
    selection.mode === "grams" ? selection.quantity / gramsBase(result) : selection.quantity;
  const scaled = scaleNutrition(result.reviewItem, factor);
  return {
    ...scaled,
    quantity: round1(selection.quantity),
    unit: selection.mode === "grams" ? "g" : result.servingLabel,
  };
}

export function resultToPlateItem(
  result: SearchResultModel,
  selection: ServingSelection,
): NewPlateItem {
  return reviewItemToPlateItem(resultToReviewItem(result, selection), result.sourceRef);
}

// Macro preview line for the card; every nutrient renders "—" when unknown.
export function macroPreviewLine(item: LoggingFlowReviewItem): string {
  const gram = (value: number | undefined) => (value === undefined ? "—" : `${round1(value)} g`);
  return `P ${gram(item.proteinG)} · C ${gram(item.carbsG)} · F ${gram(item.fatG)} · Fibre ${gram(item.fiberG)}`;
}

// ── Verified database (OFF) search — submit/debounce state machine ────────
//
// OFF is called browser-direct (per-user rate limit). The screen funnels
// explicit submits and debounced auto-searches through runOffSearch, dedupes
// normalized terms, and aborts superseded requests silently. The reducer is
// pure; runOffSearch is the only code path that invokes the client.

export type OffSearchStatus = "idle" | "loading" | "done" | "error";

export interface OffSearchState {
  readonly status: OffSearchStatus;
  readonly query: string;
  readonly results: readonly OffProductView[];
}

export const IDLE_OFF_SEARCH_STATE: OffSearchState = { status: "idle", query: "", results: [] };

export type OffSearchAction =
  | { readonly type: "submit"; readonly query: string }
  | { readonly type: "success"; readonly results: readonly OffProductView[] }
  | { readonly type: "failure" };

export function offSearchReducer(
  state: OffSearchState,
  action: OffSearchAction,
): OffSearchState {
  switch (action.type) {
    case "submit": {
      const query = action.query.trim();
      if (query.length === 0) return state;
      return { status: "loading", query, results: [] };
    }
    case "success":
      return { ...state, status: "done", results: action.results };
    case "failure":
      return { ...state, status: "error", results: [] };
  }
}

export const OFF_SEARCH_ERROR_MESSAGE =
  "The verified database could not be reached. Check your connection and try again.";

export type OffSearchFn = (args: {
  query: string;
  signal?: AbortSignal;
}) => Promise<readonly OffProductView[]>;

// Pure controller (todo-21 pattern): the ONLY caller of the OFF client.
// `signal` aborts a superseded search; an aborted request resolves SILENTLY
// (no failure dispatch) so a newer query never flashes an error state.
export async function runOffSearch(
  search: OffSearchFn,
  query: string,
  dispatch: (action: OffSearchAction) => void,
  signal?: AbortSignal,
): Promise<void> {
  const trimmed = query.trim();
  if (trimmed.length === 0) {
    console.warn("[Frontend][FoodSearch] Verified search skipped: empty query");
    return;
  }
  dispatch({ type: "submit", query: trimmed });
  try {
    const results = await search(
      signal === undefined ? { query: trimmed } : { query: trimmed, signal },
    );
    if (signal?.aborted === true) {
      console.debug("[Frontend][FoodSearch] Verified search result discarded (superseded)", {
        query: trimmed,
      });
      return;
    }
    console.log("[Frontend][FoodSearch] Verified search completed", {
      query: trimmed,
      resultCount: results.length,
    });
    dispatch({ type: "success", results });
  } catch (error: unknown) {
    if (isOffAbortError(error) || signal?.aborted === true) {
      console.debug("[Frontend][FoodSearch] Verified search aborted (superseded)", {
        query: trimmed,
      });
      return;
    }
    console.error("[Frontend][FoodSearch] Verified search failed", {
      query: trimmed,
      error: error instanceof Error ? error.message : String(error),
    });
    dispatch({ type: "failure" });
  }
}

// "Branded foods" sub-filter: products carrying a barcode.
export function filterOffResults(
  results: readonly OffProductView[],
  brandedOnly: boolean,
): readonly OffProductView[] {
  return brandedOnly ? results.filter((product) => product.hasBarcode) : results;
}
